import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useSWR from "swr";
import StoreFooter from "../StoreFooter";
import StoreTopBar from "../NewTopBar";
import { Paths } from "../../Services/EndPoints";
import { fetcher } from "../../Utils/HelperFunctions";
import useContactAvailable from "../../Hooks/useContactAvailable";

// Redesign mockup 1 — 404 catch-all. Every dead-end recovers: search,
// popular crafts, back home, or Ask Meera. See storefront-redesign-system-pages-v1.html.
// Craft names are proper nouns and stay as they are; only the Verified
// Handloom chip carries copy, so it holds a key name resolved at render.
const POPULAR_CRAFTS = [
  { label: "Kota Doria" },
  { label: "Banarasi" },
  { label: "Chanderi" },
  {
    labelKey: "notFound.verifiedHandloom",
    to: "/verified-handloom",
    hl: true,
  },
];

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
    <path d="M12 2a10 10 0 0 0-8.6 15l-1.4 5 5.1-1.3A10 10 0 1 0 12 2zm5.3 14.2c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7s-3.8-3.2-3.9-3.4c-.1-.2-1-1.3-1-2.5s.6-1.8.9-2c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.3 0 .5l-.4.5c-.2.2-.3.4-.1.6.1.3.7 1.1 1.4 1.7.9.8 1.6 1 1.9 1.2.2.1.4.1.5-.1l.7-.9c.2-.2.3-.2.6-.1l1.9.9c.3.1.4.2.5.3.1.2.1.6-.1 1.1z" />
  </svg>
);

const PageNotFound = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storeName =
    useSelector((s) => s.storeDetails?.storeName) || "The Crafts Archive";
  // Shared SWR key/fetcher → reuses the app-wide about_me cache, no extra request.
  const { data: store } = useSWR(Paths.about_me, fetcher);
  const { offline } = useContactAvailable();

  const onWhatsApp = () =>
    window.open(
      store?.mobileNumber
        ? `https://wa.me/91${store.mobileNumber}`
        : "https://wa.me/",
      "_blank",
    );

  return (
    <div className="min-h-screen bg-white font-geomanist flex flex-col">
      <StoreTopBar title={storeName} />

      <div className="flex-grow">
        {/* Hero */}
        <div className="px-5 pt-[34px] pb-[6px] text-center">
          <div className="mx-auto w-[78px] h-[78px] rounded-full bg-s-tint grid place-items-center mb-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[38px] h-[38px] text-s-primary"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </div>
          <div className="text-[13px] font-[500] tracking-[0.12em] uppercase text-ink-400">
            {t("notFound.error404")}
          </div>
          <h2 className="text-[18px] font-[500] text-ink-900 mt-1 mb-1.5">
            {t("notFound.title")}
          </h2>
          <p className="text-[12.5px] text-ink-500 leading-[1.5] max-w-[240px] mx-auto">
            {t("notFound.sub")}
          </p>
        </div>

        {/* Search bar → /search */}
        <button
          onClick={() => navigate("/search")}
          className="flex items-center gap-2 mx-4 mt-1.5 mb-0.5 w-[calc(100%-2rem)] bg-wc-gray border border-wc-border rounded-pill px-[13px] py-[10px]"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="w-[15px] h-[15px] text-ink-400"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span className="text-[12.5px] text-ink-400">
            {t("notFound.searchPlaceholder")}
          </span>
        </button>

        {/* Popular crafts */}
        <div className="text-[10.5px] font-[500] tracking-[0.04em] uppercase text-ink-400 mx-4 mt-[14px] mb-2">
          {t("notFound.popularCrafts")}
        </div>
        <div className="flex flex-wrap gap-[7px] px-4">
          {POPULAR_CRAFTS.map((c) => (
            <button
              key={c.labelKey || c.label}
              onClick={() => navigate(c.to || "/search")}
              className={`text-[12px] font-[500] rounded-pill px-3 py-[7px] border ${
                c.hl
                  ? "text-success-ink bg-[#E8F6EC] border-[#b6e3c4]"
                  : "text-ink-700 bg-white border-wc-border"
              }`}
            >
              {c.labelKey ? t(c.labelKey) : c.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-[9px] px-4 pt-4 pb-1.5">
          <button
            onClick={() => navigate("/")}
            className="w-full bg-s-primary text-white font-[500] text-[13.5px] py-[13px] rounded-[12px]"
          >
            {t("notFound.backToHome")}
          </button>
          <button
            onClick={onWhatsApp}
            disabled={offline}
            className={`w-full text-white font-[500] text-[13.5px] py-[13px] rounded-[12px] flex items-center justify-center gap-2 ${
              offline
                ? "bg-ink-400 opacity-70 cursor-not-allowed"
                : "bg-[#25D366]"
            }`}
          >
            <WhatsAppIcon />{" "}
            {offline
              ? t("notFound.offline", {
                  name: store?.firstName || t("notFound.we"),
                })
              : t("notFound.askOnWhatsApp", {
                  name: store?.firstName || t("notFound.us"),
                })}
          </button>
        </div>
        <div className="h-[14px]" />
      </div>

      <StoreFooter />
    </div>
  );
};

export default PageNotFound;
